import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { VideoData } from '@lumen5/luminary';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import { getSceneDesignDefaultText } from '../sceneDesignDefaults';
import type { LintFinding } from '../videoLinter';

import { isVoiceDrivenSceneUsingPlaceholderBaseAsset } from 'js/src/luminary/luminary-selectors';

/**
 * Warning: content still looks like an untouched placeholder. Two heuristics:
 *  - a voice-driven scene group still using the placeholder voiceover /
 *    talking-head base asset, and
 *  - an editable text asset whose text is unchanged from the template's
 *    scene-design default.
 * Both may be intentional, so they are warnings the user should confirm.
 */
export function checkPlaceholderText(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.sceneGroups.forEach((sceneGroup, sceneGroupIndex) => {
        if (isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup)) {
            findings.push({
                check: 'placeholder_text',
                severity: 'warning',
                sceneGroupIndex,
                message:
                    "This voice-driven scene group still uses the placeholder voiceover/talking-head base asset. This may be intentional — confirm you didn't forget to replace it.",
            });
        }
    });

    luminary.scenes.forEach((scene) => {
        const defaults = getSceneDesignDefaultText(scene);
        if (!defaults) {
            return;
        }
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter(
                (asset) =>
                    asset.isEditable && asset.text?.trim() && !nonRecommendable.has(asset.name),
            );
        textAssets.forEach((asset) => {
            const defaultText = defaults.get(asset.name);
            const currentText = asset.text.trim();
            if (
                defaultText !== undefined &&
                defaultText.trim().length > 0 &&
                defaultText.trim() === currentText
            ) {
                findings.push({
                    check: 'placeholder_text',
                    severity: 'warning',
                    sceneId: scene.id,
                    assetName: asset.name,
                    message: `Text "${currentText}" on this scene is unchanged from the template's default. This may be intentional — confirm you didn't forget to clear it.`,
                });
            }
        });
    });

    return findings;
}
